import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Upload, FolderOpen } from 'lucide-react';
import { useContentList } from '../hooks/useApi';
import { api } from '../services/api';
import { ContentGrid } from '../components/content/ContentGrid';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorAlert } from '../components/common/ErrorAlert';
import { EmptyState } from '../components/common/EmptyState';

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'error', label: 'Error' },
];

export function LibraryPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const { data, loading, error, refetch } = useContentList({
    status: statusFilter || undefined,
  });

  const handleDelete = useCallback(
    async (contentId: string) => {
      if (!window.confirm('Are you sure you want to delete this content and all its drafts?')) {
        return;
      }
      try {
        await api.deleteContent(contentId);
        refetch();
      } catch {
        // TODO: toast notification
      }
    },
    [refetch],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Library</h1>
          <p className="text-sm text-gray-500">
            {data?.items.length ?? 0} piece{(data?.items.length ?? 0) !== 1 ? 's' : ''} of content
          </p>
        </div>
        <Link to="/upload" className="btn-primary">
          <Upload className="mr-2 h-4 w-4" />
          Upload
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            onClick={() => setStatusFilter(filter.value)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === filter.value
                ? 'bg-brand-green text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner message="Loading content..." />
      ) : error ? (
        <ErrorAlert message={error} onRetry={refetch} />
      ) : !data?.items.length ? (
        <EmptyState
          icon={<FolderOpen className="h-12 w-12" />}
          title="No content yet"
          description="Upload videos and images to get started with social media post creation."
          action={
            <Link to="/upload" className="btn-primary">
              <Upload className="mr-2 h-4 w-4" />
              Upload Content
            </Link>
          }
        />
      ) : (
        <ContentGrid items={data.items} onDelete={handleDelete} />
      )}
    </div>
  );
}
